// Previews the range the next click in the datepicker selects while hovering
// over a day: highlights the days and fills in the From/To slots. The anchor is
// the end of the range that stays put (the start date when picking the end date,
// and vice versa). Dates are "YYYY-MM-DD" strings, so they can be compared
// lexicographically.
export const DateRangePreview = {
  mounted() {
    this.hoveredDate = null;

    this.el.addEventListener("mouseover", (e) => {
      const cell = e.target.closest("[data-date]");
      // Keep the preview in the gaps between days, but not over the rest of the picker
      const date = cell
        ? cell.dataset.date
        : e.target.closest("[data-days]")
          ? this.hoveredDate
          : null;
      if (date === this.hoveredDate) return;
      this.hoveredDate = date;
      this.applyPreview();
    });

    this.el.addEventListener("mouseleave", () => {
      this.hoveredDate = null;
      this.applyPreview();
    });
  },

  updated() {
    this.applyPreview();
  },

  applyPreview() {
    const { anchor, editing, from, to } = this.el.dataset;
    const hovered = this.hoveredDate;
    const cells = this.el.querySelectorAll("[data-date]");

    // Without a slot being edited, clicking the only selected day clears it
    if (!anchor || !hovered || (hovered === anchor && !editing)) {
      cells.forEach((cell) => delete cell.dataset.preview);
      this.setSlot("from", null);
      this.setSlot("to", null);
      this.highlightSlot(null);
      return;
    }

    const [start, end] = hovered < anchor ? [hovered, anchor] : [anchor, hovered];

    // The anchor may be in another month than the one shown
    const label = (date) =>
      date === anchor
        ? this.el.dataset.anchorLabel
        : this.el.querySelector(`[data-date="${date}"]`)?.dataset.label;

    this.setSlot("from", label(start));
    this.setSlot("to", label(end));
    // The hovered day may land in the other slot than the one being edited
    this.highlightSlot(
      hovered === anchor ? editing : hovered < anchor ? "from" : "to",
    );

    cells.forEach((cell) => {
      const date = cell.dataset.date;

      if (date >= start && date <= end) {
        cell.dataset.preview =
          start === end
            ? "single"
            : date === start
              ? "start"
              : date === end
                ? "end"
                : "range";
      } else if (from && to && date >= from && date <= to) {
        // Currently selected, but not anymore after this click
        cell.dataset.preview = "out";
      } else {
        delete cell.dataset.preview;
      }
    });
  },

  // Shows `text` in a From/To slot, or restores its server-rendered value
  setSlot(name, text) {
    const el = this.el.querySelector(`[data-slot="${name}"] [data-slot-text]`);
    if (!el) return;

    el.textContent = text || el.dataset.value;
    if (text) {
      el.dataset.previewing = "";
    } else {
      delete el.dataset.previewing;
    }
  },

  // Outlines the slot the next click changes, or restores the server-rendered outline
  highlightSlot(name) {
    this.el.querySelectorAll("[data-slot]").forEach((slot) => {
      if (name) {
        slot.dataset.highlight = slot.dataset.slot === name ? "on" : "off";
      } else {
        delete slot.dataset.highlight;
      }
    });
  },
};
